<template>
  <div class="datasets-manager">
    <div class="selections">
      <!-- Dataset selection -->
      <div class="dataset-select">
        <label for="dataset-dropdown">Choose Dataset: </label>
        <select
          id="dataset-dropdown"
          v-model="selectedDatasetId"
          @change="onDatasetChange"
        >
          <option
            v-for="ds in datasets"
            :key="ds.DatasetId"
            :value="ds.DatasetId"
          >
            {{ ds.DatasetId }} - {{ ds.CreatedOn }}
          </option>
        </select>
      </div>

      <!-- Split selection -->
      <div v-if="statsLoaded" class="split-select">
        <label for="split-dropdown">Select Split: </label>
        <select
          id="split-dropdown"
          v-model="selectedSplit"
          @change="updateCharts"
        >
          <option value="train">Train</option>
          <option value="val">Val</option>
          <option value="test">Test</option>
        </select>
      </div>
    </div>

    <!-- Charts -->
    <div v-if="statsLoaded" class="charts-section">
      <div class="chart-section-left">
        <div class="dashboard-component">
          <h1>Label Cooccurrence</h1>
          <div class="chart-left-container">
            <VChart
              :option="cooccurrenceOptions"
              class="chart-left"
              autoresize
            />
          </div>
        </div>
        <div class="chart-container">
          <div class="dashboard-component">
            <h1>Split Covariance (dB)</h1>
            <div class="chart-left-container">
              <VChart
                :option="covariateOptions"
                class="chart-left"
                autoresize
              />
            </div>
          </div>
        </div>
      </div>
      <div class="chart-container">
        <div class="dashboard-component">
          <h1>Split Sound Pressure Level Distribution (dB)</h1>
          <VChart
            :option="distributionOptions"
            class="chart-right"
            autoresize
          />
        </div>
      </div>
    </div>

    <!-- Create Dataset Button & Modal -->
    <button class="open-modal-btn" @click="openModal">Create Dataset</button>
    <div v-if="modalOpen" class="modal-overlay">
      <div class="modal">
        <h3>Create New Dataset</h3>
        <label>
          Units:
          <SearchableDropdownCheckboxes
            :options="unitOptions"
            v-model="newDataset.units"
            buttonLabel="Select Units…"
          />
        </label>
        <label>
          Start Date/Time:
          <input type="datetime-local" v-model="newDataset.start" />
        </label>
        <label>
          End Date/Time:
          <input type="datetime-local" v-model="newDataset.end" />
        </label>
        <label>
          Train Ratio:
          <input
            type="number"
            step="0.01"
            v-model.number="newDataset.train_ratio"
          />
        </label>
        <label>
          Val Ratio:
          <input
            type="number"
            step="0.01"
            v-model.number="newDataset.val_ratio"
          />
        </label>
        <label>
          Test Ratio:
          <input
            type="number"
            step="0.01"
            v-model.number="newDataset.test_ratio"
          />
        </label>
        <div class="modal-actions">
          <button @click="submitCreate" :disabled="creating">
            {{ creating ? "Creating..." : "Create" }}
          </button>
          <button @click="closeModal" :disabled="creating">Cancel</button>
        </div>
      </div>
    </div>

    <!-- Existing Datasets Table -->
    <div class="list-section">
      <table>
        <thead>
          <tr>
            <th>ID</th>
            <th>Num Samples</th>
            <th>Categories</th>
            <th>Sites</th>
            <th>Start Date</th>
            <th>End Date</th>
            <th>Created On</th>
            <th>Status</th>
            <th>Actions</th>
          </tr>
        </thead>
        <tbody>
          <tr
            v-for="ds in displayedDatasets"
            :key="ds.DatasetId + (ds.isPlaceholder ? '-pending' : '')"
            :class="{ 'pending-row': ds.isPlaceholder }"
          >
            <td>{{ ds.DatasetId }}</td>
            <td>{{ ds.NumSamples ?? "-" }}</td>
            <td>
              {{ ds.isPlaceholder ? "-" : formatCategories(ds.Categories) }}
            </td>
            <td>{{ ds.Sites ?? "-" }}</td>
            <td>{{ ds.StartDate ?? "-" }}</td>
            <td>{{ ds.EndDate ?? "-" }}</td>
            <td>{{ ds.CreatedOn ?? "-" }}</td>
            <td>{{ ds.Status }}</td>
            <td>
              <button
                v-if="!ds.isPlaceholder"
                @click="deleteDataset(ds.DatasetId)"
              >
                Delete
              </button>
            </td>
          </tr>
        </tbody>
      </table>
    </div>
  </div>
</template>

<script>
import { api_config } from "@/env.ts";
import axios from "axios";
import { getToken } from "@/keycloak-wrapper.ts";

// ECharts
import VChart from "vue-echarts";
import { use } from "echarts/core";
import { CanvasRenderer } from "echarts/renderers";
import {
  GridComponent,
  TooltipComponent,
  LegendComponent,
} from "echarts/components";
import { BarChart, BoxplotChart, HeatmapChart } from "echarts/charts";
import { UniversalTransition } from "echarts/features";

use([
  CanvasRenderer,
  GridComponent,
  TooltipComponent,
  LegendComponent,
  BarChart,
  BoxplotChart,
  HeatmapChart,
  UniversalTransition,
]);

export default {
  name: "DatasetsManager",
  components: { VChart },
  data() {
    return {
      token: null,
      datasets: [],
      unitOptions: [],
      selectedDatasetId: null,
      selectedSplit: "train",
      statsLoaded: false,
      splitStats: [],
      cooccurrence: [],
      covariateShifts: [],
      creating: false,
      modalOpen: false,
      classNames: [],
      distributionOptions: null,
      cooccurrenceOptions: null,
      covariateOptions: null,
      newDataset: {
        units: [],
        start: "",
        end: "",
        train_ratio: 0.8,
        val_ratio: 0.1,
        test_ratio: 0.1,
      },
      createJob: {
        jobId: null,
        state: null,
        error: null,
      },
      pollInterval: null,
    };
  },
  computed: {
    displayedDatasets() {
      const list = [...this.datasets];
      if (this.createJob.jobId && this.createJob.state !== "completed") {
        list.unshift({
          DatasetId: this.createJob.jobId,
          NumSamples: null,
          Categories: null,
          Sites: null,
          StartDate: null,
          EndDate: null,
          CreatedOn: null,
          Status: this.createJob.state,
          isPlaceholder: true,
        });
      }
      return list;
    },
  },
  async mounted() {
    this.token = await getToken();
    await this.fetchDatasets();
    await this.fetchStats();
  },
  beforeUnmount() {
    if (this.pollInterval) clearInterval(this.pollInterval);
  },
  deactivated() {
    if (this.pollInterval) clearInterval(this.pollInterval);
  },
  methods: {
    authHeaders() {
      return {
        "Content-Type": "application/json",
        Authorization: `Bearer ${this.token}`,
      };
    },
    async fetchDatasets() {
      try {
        const { data } = await axios.get(`${api_config.ml_backend}/datasets`, {
          headers: this.authHeaders(),
        });
        this.datasets = data.datasets;
        this.selectedDatasetId = Math.max(
          ...this.datasets.map((item) => item.DatasetId)
        );
      } catch (e) {
        console.error("Error fetching datasets", e);
      }
    },
    async fetchStats() {
      if (!this.selectedDatasetId) return;
      this.statsLoaded = false;
      try {
        const { data } = await axios.get(
          `${api_config.ml_backend}/datasets/${this.selectedDatasetId}/stats`,
          { headers: this.authHeaders() }
        );
        this.splitStats = data.split_distribution;
        this.cooccurrence = data.cooccurrence;
        this.covariateShifts = data.covariate_shifts;
        this.classNames = Array.from(
          new Set(data.split_distribution.map((item) => item.ClassName))
        );
        this.statsLoaded = true;
        this.updateCharts();
      } catch (e) {
        console.error("Error fetching stats", e);
      }
    },
    async onDatasetChange() {
      await this.fetchStats();
    },
    selectRow(id) {
      this.selectedDatasetId = id;
      this.fetchStats();
    },
    formatCategories(jsonStr) {
      try {
        const arr = JSON.parse(jsonStr);
        return Array.isArray(arr) ? arr.join(", ") : jsonStr;
      } catch {
        return jsonStr;
      }
    },
    openModal() {
      this.modalOpen = true;
    },
    closeModal() {
      this.modalOpen = false;
      this.resetForm();
    },
    resetForm() {
      this.newDataset = {
        site: "",
        start: "",
        end: "",
        train_ratio: 0.8,
        val_ratio: 0.1,
        test_ratio: 0.1,
      };
    },
    async submitCreate() {
      this.creating = true;
      try {
        const payload = {
          site: this.newDataset.site || null,
          start: this.newDataset.start
            ? new Date(this.newDataset.start).toISOString()
            : null,
          end: this.newDataset.end
            ? new Date(this.newDataset.end).toISOString()
            : null,
          train_ratio: this.newDataset.train_ratio,
          val_ratio: this.newDataset.val_ratio,
          test_ratio: this.newDataset.test_ratio,
        };
        const { data } = await axios.post(
          `${api_config.ml_backend}/datasets`,
          payload,
          {
            headers: this.authHeaders(),
          }
        );
        this.createJob.jobId = data.jobId;
        this.createJob.state = data.state;
        this.createJob.error = null;
        this.closeModal();
        this.startPollingStatus();
      } catch (e) {
        console.error("Error creating dataset", e);
        alert(`Error: ${e.response?.data?.detail || e.message}`);
      } finally {
        this.creating = false;
      }
    },
    async startPollingStatus() {
      if (this.pollInterval) return;
      this.pollInterval = setInterval(async () => {
        try {
          const { data } = await axios.get(
            `${api_config.ml_backend}/datasets/status`,
            { headers: this.authHeaders() }
          );
          this.createJob.state = data.state;
          if (data.error) this.createJob.error = data.error;
          if (data.state === "completed" || data.state === "failed") {
            clearInterval(this.pollInterval);
            this.pollInterval = null;
            if (data.state === "completed") {
              await this.fetchDatasets();
            } else {
              alert(`Dataset creation failed: ${data.error}`);
            }
            this.createJob = { jobId: null, state: null, error: null };
          }
        } catch (err) {
          console.error("Error polling dataset status", err);
        }
      }, 10000);
    },
    async deleteDataset(id) {
      if (!confirm(`Delete dataset ${id}?`)) return;
      try {
        await axios.delete(`${api_config.ml_backend}/datasets/${id}`, {
          headers: this.authHeaders(),
        });
        this.fetchDatasets();
      } catch (e) {
        console.error("Error deleting dataset", e);
        alert(
          `Error deleting dataset: ${e.response?.data?.detail || e.message}`
        );
      }
    },
    distributionOption() {
      // 1) filter & map
      const recs = this.splitStats
        .filter((x) => x.Split === this.selectedSplit)
        .map((rec) => {
          const Q1 = rec.spl_Q1;
          const median = rec.spl_Q2;
          const Q3 = rec.spl_Q3;
          const IQR = Q3 - Q1;
          const lower = Q1 - 1.5 * IQR;
          const upper = Q3 + 1.5 * IQR;
          const min = rec.spl_Min != null ? rec.spl_Min : lower;
          const max = rec.spl_Max != null ? rec.spl_Max : upper;
          return {
            cls: rec.ClassName,
            stats: [min, Q1, median, Q3, max],
            count: rec.Count, // rename if your field differs
          };
        });

      // 2) sort descending by count
      recs.sort((a, b) => b.count - a.count);

      // 3) unzip
      const names = recs.map((r) => r.cls);
      const stats = recs.map((r) => r.stats);
      const counts = recs.map((r) => r.count);

      // 4) two stacked grids, 60% vs 30%
      this.distributionOptions = {
        tooltip: {
          trigger: "axis",
          axisPointer: { type: "shadow" },
        },
        legend: {
          data: ["SPL Distribution", "Sample Count"],
        },
        grid: [
          { left: "40px", right: "30px", top: "30px", height: "50%" },
          { left: "40px", right: "30px", bottom: "90px", height: "25%" },
        ],
        xAxis: [
          {
            type: "category",
            gridIndex: 0,
            data: names,
            axisLabel: { show: false, rotate: 90, interval: 0 },
            axisTick: { show: false },
            axisLine: { show: false },
          },
          {
            type: "category",
            gridIndex: 1,
            data: names,
            boundaryGap: true,
            axisLabel: {
              rotate: 90,
              interval: 0,
              overflow: "truncate",
              formatter: (val) => val, // explicit to avoid any implicit formatting suppression
            },
            axisTick: { alignWithLabel: true },
            axisLine: { show: true },
          },
        ],
        yAxis: [
          {
            type: "value",
            name: "dB",
            splitArea: { show: true },
            gridIndex: 0,
          },
          {
            type: "value",
            name: "Count",
            gridIndex: 1,
            nameLocation: "end",
            nameGap: 10,
            nameTextStyle: { padding: [0, 0, 20, 0] },
          },
        ],
        series: [
          {
            name: "SPL Distribution",
            type: "boxplot",
            data: stats,
            xAxisIndex: 0,
            yAxisIndex: 0,
          },
          {
            name: "Sample Count",
            type: "bar",
            data: counts,
            xAxisIndex: 1,
            yAxisIndex: 1,
          },
        ],
      };
    },
    cooccurrenceOption() {
      const names = this.classNames;
      const filtered = this.cooccurrence.filter(
        (c) => c.Split === this.selectedSplit
      );
      const maxVal = Math.max(...filtered.map((c) => c.CooccurrenceCount), 0);
      const data = filtered.map((c) => [
        names.indexOf(c.ClassA),
        names.indexOf(c.ClassB),
        c.CooccurrenceCount,
      ]);
      this.cooccurrenceOptions = {
        tooltip: { position: "top" },
        grid: {
          top: "0px",
          left: "90px",
          right: "40px",
          bottom: "90px",
        },
        xAxis: {
          type: "category",
          data: names,
          splitArea: { show: true },
          axisLabel: { rotate: 90, interval: 0 },
        },
        yAxis: { type: "category", data: names, splitArea: { show: true } },
        visualMap: {
          min: 0,
          max: maxVal,
          orient: "vertical",
          right: 0,
          top: 0,
          bottom: 30,
          // calculable: true,
          realtime: false,
          text: [`${maxVal}`, "0"],
          // dimension: 2, // the value dimension in heatmap data
          // itemWidth: 20,
          // itemHeight: 120,
          // formatter: (value) => `${value}`,
        },
        series: [
          {
            name: "Co-occurrence",
            type: "heatmap",
            data,
            emphasis: { itemStyle: { borderColor: "#fff", borderWidth: 1 } },
          },
        ],
      };
    },
    covariateOption() {
      const names = this.classNames;
      const pairs = [
        ["train", "val"],
        ["train", "test"],
        ["val", "test"],
      ];
      const series = pairs.map(([A, B]) => ({
        name: `${A}-${B}`,
        type: "bar",
        data: names.map((cls) => {
          const rec = this.covariateShifts.find(
            (x) => x.Class === cls && x.SplitA === A && x.SplitB === B
          );
          return rec ? rec.MeanDelta : 0;
        }),
      }));
      this.covariateOptions = {
        tooltip: { trigger: "axis" },
        legend: { data: series.map((s) => s.name) },
        xAxis: {
          type: "category",
          data: names,
          axisLabel: { rotate: 90, interval: 0 },
        },
        yAxis: {
          type: "value",
        },
        grid: {
          top: "50px",
          left: "30px",
          right: "0px",
          bottom: "90px",
        },
        series,
      };
    },
    updateCharts() {
      this.distributionOption();
      this.cooccurrenceOption();
      this.covariateOption();
    },
  },
};
</script>

<style scoped>
.selections {
  display: flex;
  gap: var(--spacing-ml);
  align-content: center;
}

.selections input,
select {
  height: 2em;
  cursor: pointer;
}

button {
  background-color: var(--primary-color);
  border: 0;
  border-radius: var(--radius-md);
  color: var(--colour-bg-light);
  cursor: pointer;
  margin: 0;
}
button:hover {
  background-color: var(--color-primary-hover);
}

.open-modal-btn {
  margin-top: 0.4em;
  margin-bottom: 0.4em;
}
.modal-overlay {
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background: rgba(0, 0, 0, 0.5);
  display: flex;
  align-items: center;
  justify-content: center;
}
.modal {
  background: white;
  padding: 1em;
  border-radius: var(--radius-md);
  min-width: 300px;
}
.modal label {
  display: block;
  margin: 0.5em 0;
}
.modal-actions {
  margin-top: 1em;
  text-align: right;
}
.list-section table {
  width: 100%;
  border-collapse: collapse;
}
.list-section th,
.list-section td {
  border: 1px solid var(--colour-border);
  padding: 0.5em;
}
.charts-section {
  width: 100%;
  height: 37.5rem;
  display: grid;
  grid-template-columns: 2fr 3fr;
  gap: 0.1875rem;
}
.chart-section-left {
  display: grid;
  grid-template-rows: 1fr 1fr;
  height: 37.5rem;
  gap: 0.1875rem;
}
.chart-container {
  width: 100%;
  height: 100%;
}
.chart-left-container {
  height: 15.625rem;
  width: 100%;
}
.dashboard-component {
  height: 100%;
  width: 100%;
  background-color: #fdfdfd;
  border: 1px solid #aaa;
  border-radius: var(--radius-md);
  padding: 0.1875rem;
}
.dashboard-component h1 {
  margin: 0;
  padding: 0;
  margin-bottom: 0.2em;
}
.chart-right {
  height: 34.375rem;
  width: 100%;
}
.chart-left {
  height: 15.625rem;
  width: 100%;
}
.pending-row {
  background-color: #fff8dc;
  font-style: italic;
}
</style>
